
import { useState } from "react";
import { Card, Badge, type ToneInput } from "@weretradeit/ui-react";
import { integrationEvaluations, type IntegrationEvaluation } from "../data/integrationEvaluations";

// Enhanced markdown formatter with image, graph, and embed support
function formatRichContent(markdown: string): string {
  let html = markdown;

  // Headers (h1-h4)
  html = html.replace(/^#### (.*$)/gim, '<h4>$1</h4>');
  html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
  html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
  html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');

  // Bold
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

  // Lists
  html = html.replace(/^\* (.*$)/gim, '<li>$1</li>');
  html = html.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');
  html = html.replace(/^- (.*$)/gim, '<li>$1</li>');

  // Checkboxes (GitHub-style task lists)
  html = html.replace(/- \[ \] (.*$)/gim, '<li style="list-style: none;"><input type="checkbox" disabled /> $1</li>');
  html = html.replace(/- \[x\] (.*$)/gim, '<li style="list-style: none;"><input type="checkbox" checked disabled /> $1</li>');

  // Code blocks (triple backticks)
  html = html.replace(/```(\w+)?\n([\s\S]*?)```/g, (match, lang, code) => {
    const language = lang || 'plaintext';
    return `<pre><code class="language-${language}">${code.trim()}</code></pre>`;
  });

  // Inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Tables
  html = html.replace(/\|(.+)\|\n\|[-:\s|]+\|\n((?:\|.+\|\n?)+)/g, (match: string, header: string, rows: string) => {
    const headerCells = header.split('|').filter((cell: string) => cell.trim()).map((cell: string) =>
      `<th>${cell.trim()}</th>`
    ).join('');
    const rowsHtml = rows.trim().split('\n').map((row: string) => {
      const cells = row.split('|').filter((cell: string) => cell.trim()).map((cell: string) =>
        `<td>${cell.trim()}</td>`
      ).join('');
      return `<tr>${cells}</tr>`;
    }).join('');
    return `<table><thead><tr>${headerCells}</tr></thead><tbody>${rowsHtml}</tbody></table>`;
  });

  // Images (NEW: Support for markdown images)
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, alt, src) => {
    return `<img src="${src}" alt="${alt}" style="max-width: 100%; height: auto; border-radius: 8px; margin: 16px 0;" />`;
  });

  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

  // Horizontal rules
  html = html.replace(/^---$/gim, '<hr />');

  // Line breaks
  html = html.replace(/\n/g, '<br />');

  return html;
}

export const IntegrationsWidget = () => {
  const [selectedEval, setSelectedEval] = useState<IntegrationEvaluation | null>(null);

  const sortedEvaluations = [...integrationEvaluations].sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  const getPriorityTone = (priority: IntegrationEvaluation["priority"]): ToneInput => {
    switch (priority) {
      case "IMMEDIATE":
        return "success";
      case "NEXT-SPRINT":
        return "info";
      case "BACKLOG":
        return "neutral";
      default:
        return "neutral";
    }
  };

  /* `color` war NIE eine Eigenschaft von Badge — es ging als gleichnamiges
     HTML-Attribut durch (BadgeProps erweitert HTMLAttributes), und `color` auf
     einem <span> ignoriert jeder Browser. Die neun Abzeichen waren also schon
     vorher farblos; tsc schwieg, weil das Attribut existiert.

     Uebersetzt auf `tone`, den einzigen Farbweg, den v3 kennt. Ein Verlust
     steht dabei fest: v3 hat kein Lila, AI/ML und Infrastructure fallen beide
     auf `info`. Eine erfundene Farbe waere genau das, wogegen v3 antritt. */
  const getCategoryTone = (category: string): ToneInput => {
    if (category.includes("AI/ML")) return "info";
    if (category.includes("Infrastructure")) return "info";
    if (category.includes("Crypto")) return "warn";
    return "neutral";
  };

  if (selectedEval) {
    return (
      <Card
        title={selectedEval.title}
        kicker={
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "8px" }}>
            <Badge tone={getCategoryTone(selectedEval.category)}>
              {selectedEval.category}
            </Badge>
            <Badge tone={getPriorityTone(selectedEval.priority)}>
              {selectedEval.priority}
            </Badge>
            <Badge tone="success">
              ${selectedEval.profitScore.toFixed(2)}/hour profit
            </Badge>
            <Badge tone="info">
              ${selectedEval.totalValue}/month value
            </Badge>
            <Badge tone="neutral">
              {selectedEval.implementationTime}h implementation
            </Badge>
          </div>
        }
        actions={
          <button
            onClick={() => setSelectedEval(null)}
            style={{
              padding: "8px 16px",
              background: "var(--color-primary)",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            ← Back to Integrations
          </button>
        }
      >
        <div
          style={{
            fontSize: "14px",
            lineHeight: "1.6",
            color: "var(--color-text-secondary)",
          }}
          dangerouslySetInnerHTML={{
            __html: formatRichContent(selectedEval.content)
          }}
        />
      </Card>
    );
  }

  return (
    <Card
      title="Integrations & Gains"
      kicker="External provider evaluations and profit opportunities"
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {sortedEvaluations.map((evaluation) => (
          <div
            key={evaluation.id}
            style={{
              padding: "16px",
              border: "1px solid var(--color-border)",
              borderRadius: "8px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              background: "var(--color-background)",
            }}
            onClick={() => setSelectedEval(evaluation)}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "var(--color-primary)";
              e.currentTarget.style.background = "var(--color-background-hover)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "var(--color-border)";
              e.currentTarget.style.background = "var(--color-background)";
            }}
          >
            <div style={{ marginBottom: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                <span style={{ fontSize: "11px", color: "var(--color-text-tertiary)" }}>
                  {new Date(evaluation.date).toLocaleDateString("de-DE", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </span>
                <span style={{ color: "var(--color-border)" }}>•</span>
                <span style={{ fontSize: "11px", fontWeight: "500", color: "var(--color-text-secondary)" }}>
                  {evaluation.provider}
                </span>
              </div>
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "var(--color-text-primary)",
                  margin: "4px 0",
                }}
              >
                {evaluation.title}
              </h3>
            </div>

            <p
              style={{
                fontSize: "13px",
                color: "var(--color-text-secondary)",
                marginBottom: "12px",
                lineHeight: "1.5",
              }}
            >
              {evaluation.summary}
            </p>

            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
              <Badge tone={getCategoryTone(evaluation.category)}>
                {evaluation.category}
              </Badge>
              <Badge tone={getPriorityTone(evaluation.priority)}>
                {evaluation.priority}
              </Badge>
              <Badge tone="success">
                ${evaluation.profitScore.toFixed(2)}/hour
              </Badge>
              <Badge tone="info">
                ${evaluation.totalValue}/month
              </Badge>
            </div>

            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
              {evaluation.tags.slice(0, 5).map((tag) => (
                <span
                  key={tag}
                  style={{
                    fontSize: "11px",
                    padding: "2px 8px",
                    background: "var(--color-background-subtle)",
                    borderRadius: "12px",
                    color: "var(--color-text-tertiary)",
                  }}
                >
                  {tag}
                </span>
              ))}
              {evaluation.tags.length > 5 && (
                <span
                  style={{
                    fontSize: "11px",
                    padding: "2px 8px",
                    color: "var(--color-text-tertiary)",
                  }}
                >
                  +{evaluation.tags.length - 5} more
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};
